import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectSupportAgent } from '../../features/auth/authSlice';

const DEFAULT_SCRIPT_ID = 'capacity-concierge-v2-script';
// Element the concierge script creates on document.body and renders the widget into.
const CONCIERGE_ELEMENT_ID = 'capacity-concierge-v2-element';

declare global {
    interface Window {
        CapacityConciergeActionV2?: {
            destroyAction?: (param: { token: string }) => void;
        };
    }
}

const removeConcierge = (scriptId: string, conciergeToken?: string) => {
    // Lets the widget stop its sockets, wipe its session storage and unmount itself.
    // It only acts when the token matches the concierge token of the loaded script.
    if (conciergeToken) {
        try {
            window.CapacityConciergeActionV2?.destroyAction?.({ token: conciergeToken });
        } catch (err) {
            console.error('Failed to destroy Capacity Concierge.', err);
        }
    }

    // Fallback for when the widget has not finished loading and the destroy action is not registered yet.
    document.getElementById(scriptId)?.remove();
    document.getElementById(DEFAULT_SCRIPT_ID)?.remove();
    document.getElementById(CONCIERGE_ELEMENT_ID)?.remove();
};

/**
 * Loads the Capacity Concierge (Support Agent) widget with the token returned by the HCRx login/refresh.
 * The API reports the concierge as disabled when the user's email domain is not allowed or the user
 * does not exist in Capacity; nothing is displayed then. Logging out clears the token, which removes it.
 */
const SupportAgentConcierge = () => {
    const supportAgent = useSelector(selectSupportAgent);

    const enabled = !!supportAgent?.conciergeEnabled;
    const userToken = supportAgent?.userToken;
    const scriptSrc = supportAgent?.concierge?.scriptSrc;
    const conciergeToken = supportAgent?.concierge?.conciergeToken;
    const scriptId = supportAgent?.concierge?.scriptId || DEFAULT_SCRIPT_ID;

    useEffect(() => {
        if (!enabled || !userToken || !scriptSrc || !conciergeToken) {
            return;
        }

        removeConcierge(scriptId);

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = scriptSrc;
        script.defer = true;
        script.setAttribute('data-concierge-token', conciergeToken);
        script.setAttribute('data-user-token', userToken);
        script.onerror = () => console.error('Failed to load Capacity Concierge script.');

        document.head.appendChild(script);

        return () => removeConcierge(scriptId, conciergeToken);
    }, [enabled, userToken, scriptSrc, conciergeToken, scriptId]);

    return null;
};

export default SupportAgentConcierge;
